import { FC, useCallback, useMemo, useState, useEffect } from 'react'
import { LayoutGroup, motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Text } from '@lounge-fe/ui-kit/src/components/Text'
import { ProductCard } from '@lounge-fe/ui-kit/src/patterns/ProductCard'
import { useCollectionContext } from '../Collection'
import { Skeleton } from '@lounge-fe/ui-kit'
import { CollectionProductsProps, PromoCardConfiguration } from './types'
import { useNormalisedPromoCards } from './useNormalisedPromoCards'
import { ProductGrid } from './ProductGrid'
import { useGridSize } from '../useGridSize'
import { useTranslation } from 'react-i18next'
import { useShopify } from '@/hooks/useShopify'
import {
  ClickedProductAfterSearchEvent,
  ConvertedObjectIDsAfterSearchEvent,
  useSearchInsights,
} from '@/hooks/useSearchInsights'
import { NoResults } from './NoResults'
import { MemoisedProducts } from './MemoisedProducts'
import { useSearchParams } from '@/hooks/useSearchParams'
import { LoadButton } from './LoadButton'
import { useAlgoliaDataExtension } from '@/features/product/hooks/useAlgoliaDataExtension/useAlgoliaDataExtension'
import { ExtendedProductHit } from '@/features/product/types'
import classNames from 'classnames'

const Products: FC<CollectionProductsProps> = ({
  promoCards,
  targetPageSize,
  isInitialLoading,
  hasNextPage,
  hasPreviousPage,
  isFetching,
  isFetchingNextPage,
  fetchNextPage,
  fetchPreviousPage,
  pages,
  handle = false,
  enableInfiniteScroll = true,
}) => {
  const { searchParams, replace } = useSearchParams()

  const [infiniteScrollRef] = useInView({
    threshold: 0.1,
    rootMargin: '0px',
    onChange: async (inView) => {
      if (inView && enableInfiniteScroll) {
        const nextPage = pages[pages.length - 1]?.nextCursor ?? 0
        const newParams = new URLSearchParams(searchParams)
        newParams.set('page', nextPage.toString())
        await fetchNextPage()
        replace({ page: nextPage })
      }
    },
  })
  const { productContainerRef } = useCollectionContext()
  const { t } = useTranslation()
  const { settings } = useShopify()
  const { clickedProductAfterSearch, convertedObjectIDsAfterSearch } =
    useSearchInsights()

  const hasResults = pages.some(({ products }) => products.length > 0)

  const currentCount = useMemo(
    () => pages.reduce((accum, { products }) => accum + products.length, 0),
    [pages],
  )

  const totalCount = useMemo(
    () => pages[pages.length - 1]?.totalCount ?? 0,
    [pages],
  )

  const { normalisedPromoCards, finalIndexShift } = useNormalisedPromoCards(
    promoCards,
    targetPageSize,
  )

  const products = useMemo(
    () =>
      pages
        .flatMap(({ products, queryID, startOffset }) =>
          products.map((product, i) => ({
            ...product,
            queryID,
            index: startOffset + i,
          })),
        )
        .map((product, idx) => ({ ...product, position: idx + 1 })),
    [pages],
  )

  const extendedProducts = useAlgoliaDataExtension(products, true)

  const mergeProductsAndPromoCards = useMemo<
    (
      | ((typeof extendedProducts)[number] & { queryID?: string })
      | PromoCardConfiguration
    )[]
  >(() => {
    const mergedData: (
      | ((typeof extendedProducts)[number] & { queryID?: string })
      | PromoCardConfiguration
    )[] = [...extendedProducts]

    Object.keys(normalisedPromoCards)
      .filter(
        (key) => parseInt(key, 10) < currentCount + Math.abs(finalIndexShift),
      )
      .forEach((key) => {
        const intKey = parseInt(key, 10)
        const listIndex = products.findIndex(({ index }) => index === intKey)
        if (listIndex === -1) return
        mergedData.splice(listIndex, 0, normalisedPromoCards[intKey])
      })

    return mergedData
  }, [extendedProducts, normalisedPromoCards])

  const onProductClick = useCallback((product: ExtendedProductHit) => {
    if (product?.objectID) {
      clickedProductAfterSearch({
        objectID: product.objectID,
        eventName: ClickedProductAfterSearchEvent.Collections,
        queryID: product.queryID,
        position: product.position,
      })
    }
  }, [])

  const onProductQuickAdd = useCallback((product: ExtendedProductHit) => {
    if (product?.queryID && product?.objectID) {
      convertedObjectIDsAfterSearch({
        objectID: product.objectID,
        eventName: ConvertedObjectIDsAfterSearchEvent.Collections,
        queryID: product.queryID,
      })
    }
  }, [])

  const firstPage = pages?.[0]
  const lastPage = pages?.[pages?.length - 1]

  const currentGridSize = useGridSize((state) => state.currentGridSize)

  return (
    <div className="flex relative flex-1">
      <LayoutGroup>
        <motion.div ref={productContainerRef} />
        {hasResults || isInitialLoading ? (
          <div className="w-full">
            {hasPreviousPage && (
              <div className="flex w-full justify-center mb-8">
                <LoadButton
                  fetch={fetchPreviousPage}
                  isFetching={isFetching}
                  page={pages[0]?.prevCursor ?? 0}
                >
                  {t('action.load_previous')}
                </LoadButton>
              </div>
            )}
            <ProductGrid data-testid="product-grid" gridSize={currentGridSize}>
              {isInitialLoading ? (
                Array(targetPageSize)
                  .fill(0)
                  .map((_, idx) => (
                    <ProductCard.Skeleton
                      key={idx}
                      data-testid="product-skeleton"
                    />
                  ))
              ) : (
                <MemoisedProducts
                  onClick={onProductClick}
                  onQuickAdd={onProductQuickAdd}
                  productsOrPromoCards={mergeProductsAndPromoCards}
                  handle={handle}
                  settings={settings}
                  gridSize={currentGridSize}
                />
              )}
              {isFetchingNextPage &&
                Array(Math.round(targetPageSize / 2))
                  .fill(0)
                  .map((_, idx) =>
                    currentGridSize === 'sm' ? (
                      <Skeleton key={idx}>
                        <Skeleton.Block className="w-full aspect-[2/3]" />
                      </Skeleton>
                    ) : (
                      <ProductCard.Skeleton key={idx} />
                    ),
                  )}
            </ProductGrid>

            <div className="flex flex-col gap-y-3 items-center mb-15">
              {hasNextPage && (
                <LoadButton
                  className={classNames(enableInfiniteScroll && 'hidden')}
                  page={pages[pages.length - 1]?.nextCursor ?? 0}
                  isFetching={isFetching}
                  fetch={fetchNextPage}
                >
                  {t('action.load_more')}
                </LoadButton>
              )}
              {hasResults &&
                hasNextPage &&
                !isFetchingNextPage &&
                enableInfiniteScroll && (
                  <div
                    className="absolute bottom-0 left-0 right-0 h-screen z-[-999]"
                    ref={infiniteScrollRef}
                  />
                )}
              <Text variant="body-sm">
                {t('general.viewing_x_y_of_total_type', {
                  x: firstPage?.startOffset + 1 ?? 1,
                  y: lastPage?.endOffset ?? 1,
                  total: totalCount,
                  type: t('product.products'),
                })}
              </Text>
            </div>
          </div>
        ) : (
          <NoResults />
        )}
      </LayoutGroup>
    </div>
  )
}

export default Products
